import React, {useEffect, useState} from "react";
import Table, { TableColumn } from "../../../../../components/Table";
import { BadgeGanho, BadgePerdido, TableContainer } from "../../../styles";
import { BadgeChargeback, BadgeDisputa, BadgeFinalizada } from "../../../styles";
import { EditOutlined, EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import { ChargebackItem, ChargebackTableProps } from "../../types";
import useCurrentUser from "../../../../../hooks/useCurrentUser";
import {Checkbox} from "antd";
import {CheckboxChangeEvent} from "antd/es/checkbox";

const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const acquirerMap: Record<string | number, string> = {
    [-1]: "Adquirente",
    3: "Adiq",
    10: "Rede",
    99: "Valori",
};

export const LiveTable: React.FC<ChargebackTableProps> = (
  {
    data,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onView,
    onEdit,
    onDownload,
    onSaveChanges
  }) => {
  const user = useCurrentUser();
  const isAdmin = user?.userType === 3;
  const [modifiedChargebacks, setModifiedChargebacks] = useState<
    Record<
      string,
      { refundCompleted: boolean; merchantId: string; refundValue: number; refundMethod: string }
    >
  >({});

  useEffect(() => {
    setModifiedChargebacks({});
  }, [data.records]);

  const getChargebackRefundStatus = (record: any) => {
    if (record.id in modifiedChargebacks) {
      return modifiedChargebacks[record.id];
    }
    return record.refundCompleted;
  };

  const changeChargebackRefundStatus = (
    e: CheckboxChangeEvent,
    record: ChargebackItem
  ) => {
    const isChecked = e.target.checked;

    setModifiedChargebacks((prev) => {
      const newState = { ...prev };

      if (isChecked === record.refundCompleted) {
        delete newState[record.id];
      } else {
        newState[record.id] = {
          refundCompleted: isChecked,
          merchantId: record.merchantId,
          refundValue: record.paymentValue,
          refundMethod: 'COBRANÇAS',
        };
        if (isChecked) {
          newState[record.id] = {
            refundCompleted: isChecked,
            merchantId: record.merchantId,
            refundValue: record.paymentValue,
            refundMethod: 'COBRANÇAS',
          };
        } else {
          delete newState[record.id];
        }
      }

      return newState;
    });
  };

  const changeAllChargebackRefundStatus = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;

    setModifiedChargebacks((prev) => {
      const newState = { ...prev };

      data.records
        .filter((record) => record.disputeStatus === '1')
        .forEach((record) => {
          if (isChecked && !record.refundCompleted) {
            newState[record.id] = {
              refundCompleted: isChecked,
              merchantId: record.merchantId,
              refundValue: record.paymentValue,
              refundMethod: 'COBRANÇAS',
            };
          } else if (!isChecked) {
            delete newState[record.id];
          }
        });

      return newState;
    });
  };

  const eligibleRecords = data.records.filter((record) => record.disputeStatus === '1');

  const allChecked =
    eligibleRecords.length > 0 &&
    eligibleRecords.every((record) => getChargebackRefundStatus(record));

  const someChecked = eligibleRecords.some((record) =>
    getChargebackRefundStatus(record)
  );

  // Exponha a função handleSaveChanges via ref ou retorne via callback
  const handleSaveChanges = () => {
    return Object.entries(modifiedChargebacks).map(
      ([id, values]) => ({
        id,
        values,
      })
    );
  };

// No final do render, antes do return:
  useEffect(() => {
    if (onSaveChanges) {
      onSaveChanges(handleSaveChanges);
    }
  }, [modifiedChargebacks]);

  return (
        <TableContainer>
            <Table
                dataSource={data.records}
                pagination={{
                    defaultPageSize: pageSize,
                    defaultCurrent: currentPage,
                    current: currentPage,
                    onShowSizeChange: (_, size) => onPageSizeChange(size),
                    total: data.count,
                    onChange: (pageNum) => onPageChange(pageNum),
                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
                }}
            >
                <TableColumn
                    title="Data Criação Chargeback"
                    dataIndex="createdAt"
                    key="dataCriacao"
                    render={(value: string) => formatDate(value)}
                />
                <TableColumn
                    title="Data da Transação"
                    dataIndex="paymentDate"
                    key="dataTransacao"
                    render={(value: string) => formatDate(value)}
                />
                <TableColumn
                    title="Prazo para defesa"
                    dataIndex="dateLimit"
                    key="prazoDefesa"
                    render={(value: string) => formatDate(value)}
                />
                <TableColumn title="Código do Lojista" dataIndex="merchantId" key="codigoLojista" />

                <TableColumn
                    title="Valor da Venda"
                    dataIndex="paymentValue"
                    key="valorVenda"
                    render={(value: number) => formatCurrency(value)}
                />
                <TableColumn
                    title="Valor Contestado"
                    dataIndex="chargebackValue"
                    key="valorContestado"
                    render={(value: number) => formatCurrency(value)}
                />
                <TableColumn
                    title="Valor Líquido"
                    dataIndex="liquidValue"
                    key="valorLiquido"
                    render={(value: number) => formatCurrency(value)}
                />

                <TableColumn title="NSU" dataIndex="nsu" key="nsu" />
                <TableColumn title="Cod. Autorização" dataIndex="authorization" key="codAutorizacao" />
                <TableColumn title="Bandeira" dataIndex="brand" key="bandeira" />
                <TableColumn title="Parcelas" dataIndex="parcels" key="parcelas" />
                <TableColumn title="Canal do Pagamento" dataIndex="captureChannel" key="canalPagamento" />

                <TableColumn
                    title="Prazo Para Defesa"
                    dataIndex="dateLimit"
                    key="prazoDefesa"
                    render={(value: string) => formatDate(value)}
                />
                <TableColumn
                    title="Adquirente"
                    dataIndex="acquirer"
                    key="adquirente"
                    render={(value: string | number) => acquirerMap[value] ?? value}
                />
                <TableColumn title="Solicitante" dataIndex="createdBy" key="createdBy" />

                <TableColumn
                    title="Status"
                    dataIndex="disputeStatus"
                    key="status"
                    render={(value: string) => {
                        switch (value) {
                            case "0":
                                return <BadgeDisputa>Em Disputa</BadgeDisputa>;
                            case "1":
                                return <BadgeGanho>Procedente</BadgeGanho>;
                            case "2":
                                return <BadgePerdido>Improcedente</BadgePerdido>;
                            default:
                                return <span>{value}</span>;
                        }
                    }}
                />
                <TableColumn
                    title="Detalhes"
                    key="detalhes"
                    align="center"
                    render={(_, record) => (
                        <EyeOutlined
                            style={{ cursor: "pointer", fontSize: 18, color: "#1890ff" }}
                            onClick={() => onView(record as ChargebackItem)}
                        />
                    )}
                />
                {isAdmin && onEdit && (
                    <TableColumn
                        title="Editar"
                        key="editar"
                        align="center"
                        render={(_, record) => (
                            <EditOutlined
                                style={{ cursor: "pointer", fontSize: 18, color: "#faad14" }}
                                onClick={() => onEdit(record as ChargebackItem)}
                            />
                        )}
                    />
                )}
              <TableColumn
                title="Download"
                key="download"
                align="center"
                render={(_, record) => (
                  <DownloadOutlined
                    style={{ cursor: "pointer", fontSize: 18, color: "#1890ff" }}
                    onClick={() => onDownload(record as ChargebackItem)}
                  />
                )}
              />

             {isAdmin && (
              <TableColumn
                title={
                  <div style={{ display: "flex", alignItems: "center" }}>
                    Estorno realizado
                    <Checkbox
                      style={{ marginLeft: "8px" }}
                      name="paid"
                      onChange={changeAllChargebackRefundStatus}
                      indeterminate={!allChecked && someChecked}
                      checked={allChecked}
                    />
                  </div>
                }
                render={(text: any, record: any) => (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Checkbox
                      disabled={record.disputeStatus !== "1"}
                      checked={getChargebackRefundStatus(record)}
                      name="paid"
                      onChange={(e) => changeChargebackRefundStatus(e, record)}
                    />
                  </div>
                )}
              />
            )}
            </Table>
        </TableContainer>
    );
};
